import React, { useEffect, useState, useCallback } from "react"
import { Clipboard, RefreshCw, Share2, Heart, Trash2, Edit, Plus, ArrowLeft } from "lucide-react"

// offline support
const backupQuotes = [
  { text: "Life is what happens when you're busy making other plans.", author: "John Lennon" },
  { text: "The way to get started is to quit talking and begin doing.", author: "Walt Disney" },
  { text: "I pursued a career in psychology with the intention of healing others only to realize that I, too, need healing", author: "Tshireletso Mogwere" },
  { text: "Your time is limited, so don't waste it living someone else's life.", author: "Steve Jobs" },
  { text: "If life were predictable it would cease to be life, and be without flavor.", author: "Eleanor Roosevelt" },
  { text: "If you look at what you have in life, you'll always have more.", author: "Oprah Winfrey" },
  { text: "You will face many defeats in life, but never let yourself be defeated.", author: "Maya Angelou" }
]

function App() {
  const [quote, setQuote] = useState("")
  const [author, setAuthor] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(false)

  // favorites 
  const [favs, setFavs] = useState(() => {
    const saved = localStorage.getItem("favs")
    return saved ? JSON.parse(saved) : []
  })

  const [currentView, setCurrentView] = useState(() => {
    return localStorage.getItem("view") || "quotes"
  })

  // create new quote
  const [newQuote, setNewQuote] = useState("")
  const [newAuthor, setNewAuthor] = useState("")
  const [shareOpen, setShareOpen] = useState(false)

  // save favorites & view
  useEffect(() => {
    localStorage.setItem("favs", JSON.stringify(favs))
  }, [favs])

  useEffect(() => {
    localStorage.setItem("view", currentView)
  }, [currentView])

  const getRandomQuote = () => {
    const random = Math.floor(Math.random() * backupQuotes.length)
    return backupQuotes[random]
  }

  // fetch random quote
  const getQuote = useCallback(async () => {
    setIsLoading(true)
    setError(false)
    try {
      const res = await fetch("https://type.fit/api/quotes")
      const data = await res.json()
      const random = data[Math.floor(Math.random() * data.length)]
      setQuote(random.text)
      setAuthor(random.author || "Unknown")
    } catch (err) {
      console.log("error getting quote", err)
      const fallback = getRandomQuote()
      setQuote(fallback.text)
      setAuthor(fallback.author)
      setError(true)
    } finally {
      setIsLoading(false)
      setCopied(false)
    }
  }, [])

  // copy quote
  const copyText = () => {
    navigator.clipboard.writeText(`"${quote}" - ${author}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  // add to favorites 
  const addFav = () => {
    if (favs.some(f => f.text === quote && f.author === author)) return
    const fav = { id: Date.now(), text: quote, author, date: new Date().toLocaleString() }
    setFavs([fav, ...favs])
  }

  const delFav = (id) => setFavs(favs.filter(f => f.id !== id))

  const alreadyFav = favs.some(f => f.text === quote && f.author === author)

  const saveNewQuote = () => {
    if (!newQuote.trim() || !newAuthor.trim()) {
      alert("Please fill in all fields before saving.")
      return
    }
    const q = { id: Date.now(), text: newQuote, author: newAuthor, date: new Date().toLocaleString(), custom: true }
    setFavs([q, ...favs])
    setQuote(q.text)
    setAuthor(q.author)
    setCurrentView("quotes")
    setNewQuote("")
    setNewAuthor("")
  }

  const share = (where) => {
    const q = `"${quote}" - ${author}`
    if (where === "facebook") window.open(`https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(q)}`)
    else if (where === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent(q)}`)
    else if (where === "instagram") { navigator.clipboard.writeText(q); alert("Copied! Paste this in your Instagram post.") }
    setShareOpen(false)
  }

  useEffect(() => { getQuote() }, [getQuote])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-600 to-purple-700 p-6">
      <div className="w-full max-w-md bg-white/90 rounded-2xl shadow-xl p-6">
        {/* header */}
        <div className="flex justify-between mb-4">
          <div>
            <h1 className="font-bold text-2xl text-indigo-800">Daily Inspiration</h1>
            <p className="text-sm text-gray-500">One quote at a time</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setCurrentView("create")} className="text-indigo-600 p-2 hover:bg-indigo-100 rounded-full"><Plus size={18}/></button>
            <button onClick={() => setCurrentView("favorites")} className="text-indigo-600 p-2 hover:bg-indigo-100 rounded-full"><Heart size={18}/></button>
          </div>
        </div>

        {/* content switching */}
        {currentView === "favorites" ? (
          <div>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-indigo-700">Favorites</h2>
              <button onClick={() => setCurrentView("quotes")} className="flex items-center text-indigo-600 hover:text-indigo-800 text-sm">
                <ArrowLeft size={16} className="mr-1"/> Back
              </button>
            </div>

            {favs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No favorites yet.</p>
            ) : (
              favs.map(f => (
                <div key={f.id} className="bg-indigo-50 p-3 mb-3 rounded-md">
                  <p className="italic text-gray-800 mb-1">"{f.text}"</p>
                  <p className="text-sm text-gray-600">- {f.author}</p>
                  <p className="text-xs text-gray-400">{f.date}</p>
                  <div className="flex justify-end gap-2 mt-2">
                    <button onClick={() => { setQuote(f.text); setAuthor(f.author); setCurrentView("quotes") }} className="text-indigo-600"><Edit size={14}/></button>
                    <button onClick={() => delFav(f.id)} className="text-red-500"><Trash2 size={14}/></button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : currentView === "create" ? (
          <div>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-indigo-700">Create Quote</h2>
              <button onClick={() => setCurrentView("quotes")} className="flex items-center text-indigo-600 hover:text-indigo-800 text-sm">
                <ArrowLeft size={16} className="mr-1"/> Back
              </button>
            </div>

            <textarea placeholder="Write your quote..." value={newQuote} onChange={(e)=>setNewQuote(e.target.value)} className="w-full border p-2 rounded mb-3"/>
            <input placeholder="Author name" value={newAuthor} onChange={(e)=>setNewAuthor(e.target.value)} className="w-full border p-2 rounded mb-3"/>
            <button onClick={saveNewQuote} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">Save Quote</button>
          </div>
        ) : (
          <div>
            {isLoading ? (
              <div className="text-center py-10">
                <div className="animate-spin h-8 w-8 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : (
              <>
                <blockquote className="italic text-lg text-gray-800">"{quote}"</blockquote>
                <p className="text-right mt-2 text-gray-600">— {author}</p>
                {error && <p className="text-xs text-red-500 mt-2">Offline mode using backup quotes</p>}
              </>
            )}
          </div>
        )}

        {/* footer buttons */}
        {currentView === "quotes" && (
          <div className="flex justify-between items-center mt-6 border-t pt-4">
            <button onClick={!alreadyFav ? addFav : undefined} disabled={alreadyFav} className={`p-2 rounded-full ${alreadyFav ? 'text-red-400' : 'hover:text-red-600'}`}>
              <Heart className={alreadyFav ? 'fill-red-400' : ''} />
            </button>

            <div className="flex gap-2">
              <button onClick={copyText} className="bg-gray-100 px-3 py-2 rounded hover:bg-gray-200 flex items-center gap-1">
                <Clipboard size={14}/> {copied ? "Copied!" : "Copy"}
              </button>

              <div className="relative">
                <button onClick={() => setShareOpen(!shareOpen)} className="bg-gray-100 px-3 py-2 rounded hover:bg-gray-200 flex items-center gap-1">
                  <Share2 size={14}/> Share
                </button>

                {shareOpen && (
                  <div className="absolute bottom-full right-0 bg-white shadow-lg rounded p-2 text-sm w-40">
                    <button onClick={()=>share("facebook")} className="block w-full text-left p-1 hover:bg-gray-100">Facebook</button>
                    <button onClick={()=>share("instagram")} className="block w-full text-left p-1 hover:bg-gray-100">Instagram</button>
                    <button onClick={()=>share("whatsapp")} className="block w-full text-left p-1 hover:bg-gray-100">WhatsApp</button>
                  </div>
                )}
              </div>

              <button onClick={getQuote} className="bg-indigo-600 text-white px-3 py-2 rounded hover:bg-indigo-700 flex items-center gap-1">
                <RefreshCw size={14}/> New
              </button>
            </div>
          </div>
        )}
        <p className="text-center text-gray-400 text-xs mt-4">Made with ❤️ using React</p>
      </div>
    </div>
  )
}

export default App
